import { createSlice } from '@reduxjs/toolkit';

import {
  apiGetShortcuts,
  apiCreateShortcut,
  apiDeleteShortcut,
} from '@/mastodon/api/shortcuts';
import type {
  ApiShortcutJSON,
  ApiCreateShortcutPayload,
} from '@/mastodon/api_types/shortcuts';
import { createAppAsyncThunk } from '@/mastodon/store/typed_functions';

type QueryStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface ShortcutsState {
  items: Record<string, ApiShortcutJSON>;
  order: string[];
  status: QueryStatus;
}

const initialState: ShortcutsState = {
  items: {},
  order: [],
  status: 'idle',
};

export const fetchShortcuts = createAppAsyncThunk('shortcuts/fetch', () =>
  apiGetShortcuts(),
);

export const createShortcut = createAppAsyncThunk(
  'shortcuts/create',
  (payload: ApiCreateShortcutPayload) => apiCreateShortcut(payload),
);

export const removeShortcut = createAppAsyncThunk(
  'shortcuts/remove',
  async (id: string) => {
    await apiDeleteShortcut(id);
    return id;
  },
);

const shortcutsSlice = createSlice({
  name: 'shortcuts',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchShortcuts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchShortcuts.fulfilled, (state, action) => {
        state.items = {};
        state.order = [];
        action.payload.forEach((shortcut) => {
          state.items[shortcut.id] = shortcut;
          state.order.push(shortcut.id);
        });
        state.status = 'loaded';
      })
      .addCase(fetchShortcuts.rejected, (state) => {
        state.status = 'error';
      })
      .addCase(createShortcut.fulfilled, (state, action) => {
        const shortcut = action.payload;
        if (!state.items[shortcut.id]) {
          state.order.push(shortcut.id);
        }
        state.items[shortcut.id] = shortcut;
      })
      .addCase(removeShortcut.fulfilled, (state, action) => {
        const id = action.payload;
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete state.items[id];
        state.order = state.order.filter((itemId) => itemId !== id);
      });
  },
});

export const shortcuts = shortcutsSlice.reducer;
